import { inflate } from "pako";
import { getDocument, GlobalWorkerOptions } from "pdfjs-dist/build/pdf.mjs";
import workerSrc from "pdfjs-dist/build/pdf.worker.min.mjs?url";

GlobalWorkerOptions.workerSrc = workerSrc;
import { readApprovalLetter } from "./approvalLetterGrants.js";
import { isAtlasClaim, readAtlasClaim } from "./atlasClaimLetter.js";

const PDFJS_BROWSER_OPTIONS = {
  disableWorker: false,
  useSystemFonts: true,
  isEvalSupported: false,
  cMapUrl: "/pdfjs/cmaps/",
  cMapPacked: true,
  standardFontDataUrl: "/pdfjs/standard_fonts/",
};

function decodePdfString(value = "") {
  return value
    .replace(/\\([\\()])/g, "$1")
    .replace(/\\r/g, "\r")
    .replace(/\\n/g, "\n")
    .replace(/\\t/g, "\t")
    .replace(/\\000/g, "")
    .replace(/\s+/g, " ")
    .trim();
}

async function inflateChunk(chunk) {
  const inflated = inflate(chunk);
  return new TextDecoder("latin1").decode(inflated);
}

function latin1StringToUint8Array(value = "") {
  const bytes = new Uint8Array(value.length);
  for (let i = 0; i < value.length; i += 1) {
    bytes[i] = value.charCodeAt(i) & 0xff;
  }
  return bytes;
}

function uint8ArrayToLatin1String(bytes) {
  let output = "";
  const chunkSize = 0x8000;
  for (let i = 0; i < bytes.length; i += chunkSize) {
    output += String.fromCharCode(...bytes.subarray(i, i + chunkSize));
  }
  return output;
}

export async function extractTextFromPdfFile(file) {
  const arrayBuffer = await file.arrayBuffer();
  const bytes = new Uint8Array(arrayBuffer);

  const loadingTask = getDocument({
    data: bytes,
    ...PDFJS_BROWSER_OPTIONS,
  });
  const pdf = await loadingTask.promise;
  const pieces = [];

  for (let pageNumber = 1; pageNumber <= pdf.numPages; pageNumber += 1) {
    const page = await pdf.getPage(pageNumber);
    const content = await page.getTextContent();
    const pageText = (content.items || [])
      .map((item) => cleanLine(item?.str || ""))
      .filter(Boolean)
      .join("\n");

    if (pageText) {
      pieces.push(pageText);
    }
  }

  const pdfJsText = pieces.join("\n").trim();
  if (pdfJsText) {
    return pdfJsText;
  }

  throw new Error("Could not extract readable text from the uploaded aid letter.");
}

// The letter as lines in reading order, rebuilt from each text item's position. Some PDFs
// split words into single letters, which the item-per-line text above cannot put back
// together; approval letters are read from these lines instead.
export async function extractPositionalLines(file) {
  const bytes = new Uint8Array(await file.arrayBuffer());
  const pdf = await getDocument({ data: bytes, ...PDFJS_BROWSER_OPTIONS }).promise;
  const lines = [];
  for (let pageNumber = 1; pageNumber <= pdf.numPages; pageNumber += 1) {
    const page = await pdf.getPage(pageNumber);
    const content = await page.getTextContent();
    const rows = new Map();
    for (const item of content.items || []) {
      if (!item?.str) continue;
      const y = Math.round(item.transform[5] / 2) * 2;
      if (!rows.has(y)) rows.set(y, []);
      rows.get(y).push({ x: item.transform[4], text: item.str, width: item.width || 0 });
    }
    for (const [, items] of [...rows.entries()].sort((a, b) => b[0] - a[0])) {
      items.sort((a, b) => a.x - b.x);
      let line = "";
      let end = null;
      for (const item of items) {
        if (end !== null && item.x - end > 1.5) line += item.x - end > 12 ? "   " : " ";
        line += item.text;
        end = item.x + item.width;
      }
      if (line.trim()) lines.push(line);
    }
  }
  return lines;
}

function getItemRect(item) {
  const [, , , d, e, f] = item.transform || [];
  const width = item.width || 0;
  const height = item.height || Math.abs(d) || 0;
  return {
    x1: e || 0,
    y1: f || 0,
    x2: (e || 0) + width,
    y2: (f || 0) + height,
  };
}

function rectsIntersect(itemRect, annotRect) {
  return !(
    annotRect[2] < itemRect.x1 ||
    annotRect[0] > itemRect.x2 ||
    annotRect[3] < itemRect.y1 ||
    annotRect[1] > itemRect.y2
  );
}

function toCoordinateArray(value) {
  if (!value || typeof value.length !== "number") return [];
  return Array.from(value);
}

function getAnnotationRects(annotation) {
  const quadPoints = toCoordinateArray(annotation?.quadPoints);
  if (quadPoints.length >= 8) {
    const rects = [];
    for (let i = 0; i < quadPoints.length; i += 8) {
      const points = quadPoints.slice(i, i + 8);
      if (points.length < 8) break;
      rects.push([
        Math.min(points[0], points[2], points[4], points[6]),
        Math.min(points[1], points[3], points[5], points[7]),
        Math.max(points[0], points[2], points[4], points[6]),
        Math.max(points[1], points[3], points[5], points[7]),
      ]);
    }
    return rects;
  }

  const rect = toCoordinateArray(annotation?.rect);
  if (rect.length === 4) {
    return [rect];
  }

  return [];
}

function extractTextForAnnotation(annotation, contentItems = []) {
  const rects = getAnnotationRects(annotation);
  if (rects.length === 0) return "";

  const hits = [];
  for (const item of contentItems) {
    const text = cleanLine(item?.str || "");
    if (!text) continue;
    const itemRect = getItemRect(item);
    if (rects.some((rect) => rectsIntersect(itemRect, rect))) {
      hits.push(text);
    }
  }

  return cleanLine(hits.join(" "));
}

function cleanLine(value = "") {
  return value.replace(/\s+/g, " ").trim();
}

function getLines(text = "") {
  return text
    .split("\n")
    .map(cleanLine)
    .filter(Boolean);
}

function findLineIndex(lines, predicate) {
  return lines.findIndex((line, index) => predicate(line, index));
}

function getNextMeaningfulLine(lines, startIndex, { skip = [], maxLookahead = 8 } = {}) {
  const skipSet = new Set(skip.map((item) => item.toLowerCase()));
  for (let i = startIndex + 1; i < Math.min(lines.length, startIndex + 1 + maxLookahead); i += 1) {
    const candidate = cleanLine(lines[i]);
    if (!candidate) continue;
    if (skipSet.has(candidate.toLowerCase())) continue;
    return candidate;
  }
  return "";
}

function matchAfterLabel(text, label, nextLabels = []) {
  const escaped = label.replace(/[.*+?^${}()|[\]\\]/g, "\\$&").replace(/\s+/g, "\\s+");
  const blockers = nextLabels
    .map((item) => item.replace(/[.*+?^${}()|[\]\\]/g, "\\$&").replace(/\s+/g, "\\s+"))
    .join("|");
  const regex = blockers
    ? new RegExp(`${escaped}\\s+([\\s\\S]*?)(?=\\s+(?:${blockers})|$)`, "i")
    : new RegExp(`${escaped}\\s+([\\s\\S]*?)$`, "i");
  const match = text.match(regex);
  return cleanLine(match?.[1] || "");
}

function buildCompactUpper(text = "") {
  return text.toUpperCase().replace(/[^A-Z0-9]+/g, "");
}

function normalizeAidTypesFromHighlightedText(value = "") {
  const compact = buildCompactUpper(value);
  if (!compact) return [];
  if (compact.includes("SUMMARYHEARINGLUMPSUM")) return ["Summary Single"];
  if (compact.includes("SUMMARYHEARING")) return ["Summary Single"];
  if (compact.includes("SUMMARYCONSOL")) return ["Summary Consolidated"];
  if (compact.includes("COUNTYCOURTBREACH")) return ["County Court Breach"];
  if (compact.includes("SUPREMECOURTBREACH")) return ["Supreme Court Breach"];
  return inferAidTypes(value);
}

async function extractAnnotatedFieldOverrides(file) {
  const arrayBuffer = await file.arrayBuffer();
  const bytes = new Uint8Array(arrayBuffer);
  const loadingTask = getDocument({
    data: bytes,
    ...PDFJS_BROWSER_OPTIONS,
  });
  const pdf = await loadingTask.promise;
  const overrides = {};

  for (let pageNumber = 1; pageNumber <= pdf.numPages; pageNumber += 1) {
    const page = await pdf.getPage(pageNumber);
    const [annotations, content] = await Promise.all([page.getAnnotations(), page.getTextContent()]);
    const items = content.items || [];

    for (const annotation of annotations) {
      if (annotation?.subtype === "Popup") continue;

      const comment = cleanLine(annotation?.contentsObj?.str || annotation?.contents || "");
      if (!comment) continue;

      const commentLower = comment.toLowerCase();
      const highlightedText = extractTextForAnnotation(annotation, items);

      if (commentLower.includes("aid number for the box marked aid number")) {
        overrides.aidNumber = extractAidNumber(highlightedText) || overrides.aidNumber || "";
      }

      if (commentLower.includes("ext number for the box marked ext")) {
        const extMatch = cleanLine(highlightedText).match(/\b\d+\b/);
        overrides.extensionNumber = extMatch?.[0] || overrides.extensionNumber || "";
      }

      if (commentLower.includes("application status box")) {
        overrides.applicationStatus = "Aid applied for";
      }

      if (commentLower.includes("custody status")) {
        const custodyMatch = cleanLine(highlightedText).match(/\b(YES|NO)\b/i);
        overrides.custodyStatus = cleanLine(custodyMatch?.[1] || highlightedText || overrides.custodyStatus || "");
      }

      if (commentLower.includes("aid effective date")) {
        overrides.effectiveDate = parseDate(highlightedText) || overrides.effectiveDate || "";
      }

      if (commentLower.includes("next listing date")) {
        overrides.listingDate = parseDate(highlightedText) || overrides.listingDate || "";
      }

      if (commentLower.includes("court type")) {
        overrides.court = normalizeCourt(highlightedText) || overrides.court || "";
      }

      if (commentLower.includes("court location")) {
        overrides.location = cleanLine(highlightedText) || overrides.location || "";
      }

      if (commentLower.includes("next listing type")) {
        overrides.listingTypeRaw = cleanLine(highlightedText) || overrides.listingTypeRaw || "";
      }

      if (commentLower.includes("guideline in the guideline box")) {
        overrides.guideline = cleanLine(highlightedText) || overrides.guideline || "";
      }

      if (commentLower.includes("aid type")) {
        const aidTypeList = normalizeAidTypesFromHighlightedText(highlightedText);
        if (aidTypeList.length > 0) {
          overrides.aidTypeList = aidTypeList;
          overrides.aidType = aidTypeList.join("; ");
        }
      }
    }
  }

  if (overrides.listingTypeRaw) {
    overrides.listingType = normalizeListingType(overrides.listingTypeRaw, overrides.court || "");
  }

  return overrides;
}

function extractAidNumber(text = "") {
  return cleanLine(text.match(/\b\d{2}A\d{6}\b/) ? text.match(/\b\d{2}A\d{6}\b/)[0] : "");
}

function extractExtensionNumber(lines = [], normalizedText = "", compactText = "", documentType = "") {

  // For approval letters: the VLA grant number includes the extension as "AidNumber\ExtNum"
  // e.g. "25A834943\1" or "25A834943/1"
  // In normalizedText the backslash/slash survives; in compactText it is stripped to "25A8349431"

  // PRIORITY 1: normalizedText — look for grant number with backslash or slash separator
  const grantNormMatch = normalizedText.match(/vla\s*grant\s*number[^0-9A-Z]*([0-9A-Z]+)[\\\/](\d+)/i)
    || normalizedText.match(/grant\s*number[^0-9A-Z]*([0-9A-Z]+)[\\\/](\d+)/i);
  if (grantNormMatch) {
    const extNum = grantNormMatch[2];
    if (Number(extNum) > 0) return extNum;
  }

  // PRIORITY 2: compactText — grant number has separator stripped so aid number runs into ext digit
  // Pattern: VLAGRANTNUMBER or GRANTNUMBER followed by the aid number then ext digit(s)
  // The aid number typically ends with digits; the ext digit immediately follows
  // Match: VLAGRANTNUMBER + non-extension chars + then a SINGLE trailing digit (the ext number)
  // Strategy: find the aid number from lines and then check what follows it in compactText
  const grantLine = lines.find(l => /vla\s*grant\s*number/i.test(l) || /grant\s*number/i.test(l));
  if (grantLine) {
    // Extract the value after the colon/label, e.g. "25A834943\1"
    const grantVal = grantLine.replace(/.*(?:grant\s*number\s*[:.]?\s*)/i, "").trim();
    const sepMatch = grantVal.match(/^([0-9A-Z]+)[\\\/](\d+)$/i);
    if (sepMatch) {
      const extNum = sepMatch[2];
      if (Number(extNum) > 0) return extNum;
    }
  }

  // PRIORITY 3: rawText lines — scan for any "AidNumber\Digit" or "AidNumber/Digit" pattern
  const rawGrantMatch = normalizedText.match(/\b([0-9]{2}[A-Z][0-9]+)[\\\/](\d+)\b/);
  if (rawGrantMatch) {
    const extNum = rawGrantMatch[2];
    if (Number(extNum) > 0) return extNum;
  }

  // PRIORITY 4: Application request form — look for "Extension" field label
  const extensionIndex = findLineIndex(lines, (line) => line.toLowerCase() === "extension");
  const candidate =
    getNextMeaningfulLine(lines, extensionIndex, { skip: ["Status", "Submitted", "Submitted by", "Application Source"] }) ||
    matchAfterLabel(normalizedText, "Extension", ["Status", "Submitted by", "Application Source"]);

  const lineMatch = cleanLine(candidate).match(/^\d+$/);
  if (lineMatch && Number(lineMatch[0]) >= 0) return lineMatch[0];

  const compactMatch = compactText.match(/EXTENSION(\d+)/);
  if (compactMatch && Number(compactMatch[1]) >= 0) return compactMatch[1];

  return "";
}

function extractClientFirstName(lines = [], normalizedText = "") {
  // VLA forms use several label variants across different versions
  const FIRST_NAME_LABELS = new Set(["first name", "first name *", "given name", "given name *", "given name(s)", "given names"]);
  const index = findLineIndex(lines, (line) => FIRST_NAME_LABELS.has(cleanLine(line).toLowerCase()));
  // DIAGNOSTIC
  console.log("[EMMA firstName] labelIdx:", index, "| window:", index >= 0 ? lines.slice(index, index + 8) : "NOT FOUND");
  if (index >= 0) {
    for (let i = index + 1; i <= index + 12 && i < lines.length; i++) {
      const candidate = cleanLine(lines[i]);
      if (!candidate) continue;
      // Skip single-character values (e.g. "A" section codes in VLA form headers)
      if (candidate.length <= 1) continue;
      // Skip values containing digits — file references / IDs are never names
      if (/\d/.test(candidate)) continue;
      // Skip values that look like another field label
      if (/^(surname|last name|family name|date of birth|given name|first name)/i.test(candidate)) continue;
      // Skip form instruction keywords
      if (/\b(application|request|extension|grant|please|note|legal|victoria|allows|submit|reporting|outcome|proceeding|court|tribunal|matter|seeking)\b/i.test(candidate)) continue;
      return candidate;
    }
  }
  // Fallback: regex search bounded by the next field labels
  return (
    matchAfterLabel(normalizedText, "First Name", ["Surname", "Last Name", "Family name", "Date of birth", "Date of Birth"]) ||
    matchAfterLabel(normalizedText, "Given name", ["Surname", "Last Name", "Family name", "Date of birth"])
  );
}

function extractClientLastName(lines = [], normalizedText = "") {
  // VLA forms use several label variants across different versions
  const LAST_NAME_LABELS = new Set(["surname", "surname *", "last name", "last name *", "family name", "family name *"]);
  const index = findLineIndex(lines, (line) => LAST_NAME_LABELS.has(cleanLine(line).toLowerCase()));
  // DIAGNOSTIC
  console.log("[EMMA lastName] labelIdx:", index, "| window:", index >= 0 ? lines.slice(index, index + 8) : "NOT FOUND");
  if (index >= 0) {
    for (let i = index + 1; i <= index + 12 && i < lines.length; i++) {
      const candidate = cleanLine(lines[i]);
      if (!candidate) continue;
      if (candidate.length <= 1) continue;
      if (/\d/.test(candidate)) continue;
      if (/^(first name|given name|date of birth|surname|last name|family name)/i.test(candidate)) continue;
      if (/\b(application|request|extension|grant|please|note|legal|victoria|allows|submit|reporting|outcome|proceeding|court|tribunal|matter|seeking)\b/i.test(candidate)) continue;
      return candidate;
    }
  }
  // Fallback: try multiple label names bounded by adjacent fields
  return (
    matchAfterLabel(normalizedText, "Surname", ["First Name", "Given Name", "Date of Birth", "Date of birth"]) ||
    matchAfterLabel(normalizedText, "Last Name", ["First Name", "Given Name", "Date of Birth", "Date of birth"]) ||
    matchAfterLabel(normalizedText, "Family name", ["First Name", "Given Name", "Date of Birth", "Date of birth"])
  );
}

function extractClientDateOfBirth(lines = [], normalizedText = "") {
  const index = findLineIndex(lines, (line) => line.toLowerCase() === "date of birth");
  let rawDate = "";
  if (index >= 0) {
    rawDate = getNextMeaningfulLine(lines, index, { maxLookahead: 5 }) || "";
  } else {
    rawDate = matchAfterLabel(normalizedText, "Date of Birth");
  }
  return parseDate(rawDate);
}

function extractMatterGroup(lines = [], normalizedText = "") {
  const index = findLineIndex(lines, (line) => line.toLowerCase() === "matter group");
  if (index >= 0) {
    return getNextMeaningfulLine(lines, index, { maxLookahead: 5 }) || "";
  }
  return matchAfterLabel(normalizedText, "Matter Group", ["Matter Type", "Plea", "CJEP", "CAN"]);
}

function extractPractitionerReference(lines = [], normalizedText = "") {
  const index = findLineIndex(lines, (line) => line.toLowerCase() === "practitioner's" || line.toLowerCase() === "practitioner");
  if (index >= 0) {
    const nextLine = lines[index + 1] || "";
    if (nextLine.toLowerCase() === "reference") {
      const candidate = lines[index + 2] || "";
      if (candidate && !/^(page|of|application|request|signature|date)$/i.test(candidate)) {
        return candidate.trim();
      }
    }
  }
  
  const regex = /Practitioner'?s?\s+Reference\s*[\r\n\s]+([^\r\n]+)/i;
  const match = normalizedText.match(regex);
  if (match && match[1]) {
    const candidate = match[1].trim();
    if (!/^(page|of|application|request|signature|date)$/i.test(candidate)) {
      return candidate;
    }
  }
  
  return "";
}

function normalizeMatterType(text = "", matterTypeRaw = "") {
  const upper = `${matterTypeRaw} ${text}`.toUpperCase();
  if (
    /(SUMMARY|INDICTABLE|BAIL|COMMITTAL|PLEA|SENTENCE|CONTEST|TRIAL|APPEAL|MAGISTRATES' COURT|COUNTY COURT|SUPREME COURT|COURT OF APPEAL)/.test(
      upper
    )
  ) {
    return "Criminal";
  }
  return "Other";
}

function parseDate(value = "") {
  const match = value.match(/(\d{1,2})\/(\d{1,2})\/(\d{4})/);
  if (!match) return "";
  const [, dd, mm, yyyy] = match;
  return `${yyyy}-${mm.padStart(2, "0")}-${dd.padStart(2, "0")}`;
}

function parseDateFromCompact(value = "") {
  const match = value.match(/(\d{1,2})(JANUARY|FEBRUARY|MARCH|APRIL|MAY|JUNE|JULY|AUGUST|SEPTEMBER|OCTOBER|NOVEMBER|DECEMBER)(\d{4})/);
  if (!match) return "";
  const months = {
    JANUARY: "01",
    FEBRUARY: "02",
    MARCH: "03",
    APRIL: "04",
    MAY: "05",
    JUNE: "06",
    JULY: "07",
    AUGUST: "08",
    SEPTEMBER: "09",
    OCTOBER: "10",
    NOVEMBER: "11",
    DECEMBER: "12",
  };
  const [, dd, month, yyyy] = match;
  return `${yyyy}-${months[month]}-${dd.padStart(2, "0")}`;
}

function parseWordyDate(value = "") {
  const match = value.match(/(\d{1,2})\s+(January|February|March|April|May|June|July|August|September|October|November|December)\s+(\d{4})/i);
  if (!match) return "";
  const months = {
    january: "01", february: "02", march: "03", april: "04", may: "05", june: "06",
    july: "07", august: "08", september: "09", october: "10", november: "11", december: "12"
  };
  const [, dd, mmName, yyyy] = match;
  const mm = months[mmName.toLowerCase()];
  return `${yyyy}-${mm}-${dd.padStart(2, "0")}`;
}

function parseApprovalEffectiveDate(text = "") {
  const regex = /(?:begins\s+from|effective\s+from)\s*(\d{1,2}\s+(?:January|February|March|April|May|June|July|August|September|October|November|December)\s+\d{4})/i;
  const match = text.match(regex);
  if (match) {
    return parseWordyDate(match[1]);
  }

  const compact = text.replace(/[^A-Z0-9]/ig, "").toUpperCase();
  const compactMatch = compact.match(/ASSISTANCEISEFFECTIVEFROM(\d{1,2}(?:JANUARY|FEBRUARY|MARCH|APRIL|MAY|JUNE|JULY|AUGUST|SEPTEMBER|OCTOBER|NOVEMBER|DECEMBER)\d{4})/);
  return compactMatch ? parseDateFromCompact(compactMatch[1]) : "";
}

function parseApplicationEffectiveDate(text = "", dobToIgnore = "") {
  // Extract all dates in DD/MM/YYYY format
  const matches = text.match(/\b\d{1,2}\/\d{1,2}\/\d{4}\b/g);
  if (matches && matches.length > 0) {
    const counts = {};
    matches.forEach(d => {
      const parts = d.split("/");
      if (parts.length === 3) {
        const normalized = `${parts[0].padStart(2, "0")}/${parts[1].padStart(2, "0")}/${parts[2]}`;
        if (dobToIgnore && normalized === dobToIgnore) {
          return;
        }
        counts[normalized] = (counts[normalized] || 0) + 1;
      }
    });

    let mostFrequentDate = "";
    let maxCount = 0;
    Object.keys(counts).forEach(d => {
      if (counts[d] > maxCount) {
        maxCount = counts[d];
        mostFrequentDate = d;
      }
    });

    if (maxCount >= 2) {
      return parseDate(mostFrequentDate);
    }
  }

  const appliedForMatch = text.match(/DATE\s+APPLIED\s+FOR\s*[:\s-]*\s*(\d{1,2}\/\d{1,2}\/\d{4})/i);
  if (appliedForMatch?.[1]) {
    return parseDate(appliedForMatch[1]);
  }
  const applicantMatch = text.match(/Applicant'?s\s+Signature[\s\S]*?Date\s*[\r\n\s]+(\d{1,2}\/\d{1,2}\/\d{4})/i);
  if (applicantMatch?.[1]) {
    return parseDate(applicantMatch[1]);
  }
  const practitionerMatch = text.match(/Practitioner'?s\s+Signature[\s\S]*?Date\s*[\r\n\s]+(\d{1,2}\/\d{1,2}\/\d{4})/i);
  if (practitionerMatch?.[1]) {
    return parseDate(practitionerMatch[1]);
  }
  return "";
}

function extractTemplateGuideline(lines = []) {
  const startIndex = findLineIndex(
    lines,
    (line) => cleanLine(line).toUpperCase() === "GL" || /your selection of the guideline/i.test(line)
  );
  if (startIndex < 0) return "";

  const shouldSkip = (value = "") => {
    const normalized = cleanLine(value);
    const lower = normalized.toLowerCase();
    if (!normalized) return true;
    if (
      /^(GL|\*+|YES|NO|G\d+[A-Z]?|GUIDELINE|STATEMENT|ADDITIO|NAL GUIDELINE|INFORMAT|ION|MEANS TE|ST RESULT|S|PROFESSIONA|L COSTS|ELIGIBIL|CONTRIB|PROFESSION|COSTS?|-)$/i.test(
        normalized
      )
    ) {
      return true;
    }
    if (
      lower.includes("guideline for the primary matter") ||
      lower.includes("when this application is for both bail") ||
      lower.includes("select the special circumstances guideline") ||
      lower.includes("selection of the guideline constitutes")
    ) {
      return true;
    }
    return normalized.length < 5;
  };

  for (let i = startIndex + 1; i < Math.min(lines.length, startIndex + 18); i += 1) {
    const candidate = cleanLine(lines[i]);
    if (shouldSkip(candidate)) continue;
    return candidate;
  }

  return "";
}

function cleanAndSortProfessionalCosts(rawList) {
  const cleanProfessionalCost = (cost = "") => {
    const cleaned = cleanLine(cost);
    const upper = cleaned.toUpperCase().replace(/[^A-Z0-9]+/g, "");
    if (upper === "BAIL") return "Bail";
    if (
      upper.includes("SUMMARYHEARINGLUMPSUM") || 
      upper.includes("SUMMARYHEARING") || 
      upper.includes("SUMMARYSINGLE") ||
      upper.includes("SUMMARY")
    ) {
      return "Single Summary";
    }
    if (
      upper.includes("PSYCHOLOGICALNEWASSESSMENTINCLUDINGJAIL") ||
      upper.includes("PSYCHOLOGICALASSESSMENTANDREPORTINCUSTODY") ||
      upper.includes("PSYCHOLOGICALASSESSMENTANDREPORTINCLUDINGJAILVISIT") ||
      upper.includes("PSYCHOLOGICALREPORTWITHRISKASSESSMENT")
    ) {
      return "Psychological Assessment - In Custody";
    }
    if (
      upper.includes("PSYCHOLOGICALNEWASSESSMENT") ||
      upper.includes("PSYCHOLOGICALASSESSMENTANDREPORTNOTINCUSTODY")
    ) {
      return "Psychological Assessment - Out of Custody";
    }
    if (upper.includes("PSYCHIATRICNEWASSESSMENTINCLUDINGJAIL")) {
      return "Psychiatric Assessment - In Custody";
    }
    if (upper.includes("PSYCHIATRICNEWASSESSMENT")) {
      return "Psychiatric Assessment - Out of Custody";
    }
    if (upper.includes("EXTINCREASETOCONSOL") || upper.includes("INCREASETO") || upper.includes("CONSOL")) {
      return "EXT - Increase to consol";
    }
    return cleaned;
  };

  const mapped = rawList.map(cleanProfessionalCost).filter(Boolean);
  const unique = Array.from(new Set(mapped));

  const order = {
    "Single Summary": 1,
    "EXT - Increase to consol": 2,
    "Bail": 3,
    "Psychological Assessment - In Custody": 4,
    "Psychological Assessment - Out of Custody": 5,
    "Psychiatric Assessment - In Custody": 6,
    "Psychiatric Assessment - Out of Custody": 7
  };

  return unique.sort((a, b) => {
    const valA = order[a] || 99;
    const valB = order[b] || 99;
    return valA - valB;
  });
}

function formatProfessionalCostsList(list = []) {
  if (!Array.isArray(list) || list.length === 0) return "";
  const cleaned = list.map(item => `${item || ""}`.trim()).filter(Boolean);
  if (cleaned.length === 0) return "";
  if (cleaned.length === 1) return cleaned[0];
  if (cleaned.length === 2) return `${cleaned[0]} and ${cleaned[1]}`;
  return `${cleaned.slice(0, -1).join(", ")} and ${cleaned[cleaned.length - 1]}`;
}

function extractAllProfessionalCosts(lines = [], normalizedText = "") {
  const list = [];
  const compact = buildCompactUpper(normalizedText);

  // 1. Check for B - Bail
  const bIndex = findLineIndex(lines, (line) => cleanLine(line).toUpperCase() === "B");
  if (bIndex >= 0) {
    const nextLine = getNextMeaningfulLine(lines, bIndex, { maxLookahead: 3 });
    if (nextLine.toLowerCase() === "bail") {
      list.push("Bail");
    }
  } else if (compact.includes("BBAIL")) {
    list.push("Bail");
  }

  // 2. Check for MC - Professional costs
  const mcIndex = findLineIndex(lines, (line) => cleanLine(line).toUpperCase() === "MC");
  if (mcIndex >= 0) {
    for (let i = mcIndex + 1; i < Math.min(lines.length, mcIndex + 10); i++) {
      const line = cleanLine(lines[i]);
      if (!line) continue;
      const lower = line.toLowerCase();
      if (lower === "professional costs" || lower === "professional cost" || lower === "mc" || line === "-") {
        continue;
      }
      list.push(line);
      break;
    }
  }

  // 3. Scan compact text for common scope of work/disbursement entries
  if (compact.includes("PSYCHOLOGICALNEWASSESSMENTINCLUDINGJAIL")) {
    list.push("psychological new assessment including jail");
  } else if (compact.includes("PSYCHOLOGICALNEWASSESSMENT")) {
    list.push("psychological new assessment");
  } else if (compact.includes("PSYCHOLOGICALREPORTWITHRISKASSESSMENT")) {
    list.push("psychological report with risk assessment");
  }
  
  if (compact.includes("PSYCHIATRICNEWASSESSMENTINCLUDINGJAIL")) {
    list.push("psychiatric new assessment including jail");
  } else if (compact.includes("PSYCHIATRICNEWASSESSMENT")) {
    list.push("psychiatric new assessment");
  }

  const rawList = list.filter(Boolean);
  return cleanAndSortProfessionalCosts(rawList);
}


function extractTemplateAidType(lines = []) {
  const startIndex = findLineIndex(
    lines,
    (line) =>
      cleanLine(line).toUpperCase() === "MC" ||
      /please check that you have the correct grant of aid/i.test(line)
  );
  if (startIndex < 0) return [];

  for (let i = startIndex + 1; i < Math.min(lines.length, startIndex + 40); i += 1) {
    const candidate = cleanLine(lines[i]);
    const compact = buildCompactUpper(candidate);
    if (!candidate) continue;

    if (compact.includes("SUMMARYHEARINGLUMPSUM") || compact.includes("SUMMARYHEARING")) {
      return ["Summary Single"];
    }
    // Only match Consolidated when the grant type NAME itself contains "SUMMARY" —
    // not when "CONSOLIDATED" appears in a fee item like "CONSOLIDATED PLEA PREPARATION"
    if (compact.includes("SUMMARYCONSOL") || compact.includes("SUMMARYCONSOLIDATED")) {
      return ["Summary Consolidated"];
    }
    // Indictable committal/plea scope heading
    if (compact.includes("COMMITTALMENTIONGENERALPREPARATION")) {
      // Don't return early — let inferAidTypesFromLines collect all sections
      break;
    }
    // Contested committal heading
    if (compact.includes("CONTESTEDCOMMITTALHEARING")) {
      break;
    }
  }

  return [];
}

function extractTemplateListingType(rawValue = "", courtValue = "") {
  const cleaned = cleanLine(rawValue);
  if (!cleaned) return "";
  return cleaned;
}

function normalizeCourt(value = "") {
  if (/magistrates/i.test(value)) return "Magistrates' Court";
  if (/children/i.test(value)) return "Children's Court";
  if (/county/i.test(value)) return "County Court";
  if (/supreme/i.test(value)) return "Supreme Court";
  if (/appeal/i.test(value)) return "Court of Appeal";
  return value ? "Other" : "";
}

function normalizeListingType(value = "", courtValue = "") {
  const normalized = cleanLine(value).toUpperCase();
  const normalizedCourt = cleanLine(courtValue).toUpperCase();
  if (!normalized) return "";
  if (normalized.includes("CONTESTED HEARING")) return "Contested Hearing";
  if (normalized.includes("MENTION/DIRECTION/CALLOVE")) {
    return normalizedCourt.includes("COUNTY") || normalizedCourt.includes("SUPREME")
      ? "Directions Hearing / Mention / Callover"
      : "Mention";
  }
  if (normalized.includes("DIRECTION") || normalized.includes("CALLOVE")) {
    return normalizedCourt.includes("COUNTY") || normalizedCourt.includes("SUPREME")
      ? "Directions Hearing / Mention / Callover"
      : "Mention";
  }
  if (normalized.includes("MENTION") && normalized.includes("CONTEST")) return "Contest Mention";
  if (normalized.includes("MENTION")) return "Mention";
  if (normalized.includes("PLEA")) return "Plea";
  if (normalized.includes("SENTENCE")) return "Sentence";
  if (normalized.includes("BAIL")) return "Bail Application";
  if (normalized.includes("COMMITTAL")) return "Committal";
  if (normalized.includes("APPEAL")) return "Appeal";
  if (normalized.includes("TRIAL")) return "Trial";
  if (normalized.includes("CONFERENCE")) return "Conference";
  return "Other";
}

function inferApplicationStatus(statusValue = "") {
  const normalized = cleanLine(statusValue).toUpperCase();
  if (!normalized) return "Aid submitted - application pending";
  if (normalized.includes("SUBMITTED") || normalized.includes("PENDING")) return "Aid submitted - application pending";
  if (normalized.includes("GRANTED") || normalized.includes("APPROVED")) return "Aid granted";
  if (normalized.includes("REFUSED") || normalized.includes("REJECTED")) return "Aid refused";
  return "Aid submitted - application pending";
}

function extractApplicationRequestStatus(statusRaw = "", compactText = "") {
  const normalized = cleanLine(statusRaw).toUpperCase();
  if (/(SUBMITTED|PENDING|GRANTED|APPROVED|REFUSED|REJECTED)/.test(normalized)) {
    return normalized;
  }
  if (compactText.includes("STATUSSUBMITTED")) return "SUBMITTED";
  if (compactText.includes("STATUSPENDING")) return "PENDING";
  if (compactText.includes("STATUSGRANTED") || compactText.includes("STATUSAPPROVED")) return "APPROVED";
  if (compactText.includes("STATUSREFUSED") || compactText.includes("STATUSREJECTED")) return "REFUSED";
  return "";
}

const AID_TYPE_CATALOG = [
  { label: "Summary Single", patterns: ["SUMMARYSINGLE", "SUMMARYUPTOFIVEDEFENDEDDAYSMAGISTRATESCOURT"] },
  { label: "Summary Consolidated", patterns: ["SUMMARYCONSOL", "SUMMARYCONSOLIDATED", "CONSOLIDATEDCONTESTMENTION"] },
  { label: "Summary ARC", patterns: ["SUMMARYARC"] },
  { label: "Summary Bail", patterns: ["SUMMARYBAIL"] },
  { label: "Summary - Increase to consol", patterns: ["SUMMARYINCREASETOCONSOL", "MCEXTINCREASETOCONSOL"] },
  { label: "Summary - Ground Rules hearing", patterns: ["SUMMARYGROUNDRULESHEARING"] },
  { label: "Infringements", patterns: ["INFRINGEMENTS", "INFRINGEMENT"] },
  { label: "Family Violence - Respondent", patterns: ["FAMILYVIOLENCERESPONDENT"] },
  { label: "Family Violence - Applicant", patterns: ["FAMILYVIOLENCEAPPLICANT"] },
  { label: "Personal Safety Intervention order", patterns: ["PERSONALSAFETYINTERVENTIONORDER"] },
  {
    label: "Indictable - Committal and or Plea in the County Court",
    patterns: [
      "INDICTABLECOMMITTALANDORPLEAINTHECOUNTYCOURT",
      "COMMITTALANDPLEACOUNTYCOURT",
      "INDICTABLECOUNTYPLEA",
      // Application request Scope of Work section heading:
      "COMMITTALMENTIONGENERALPREPARATION", // "COMMITTAL MENTION - GENERAL PREPARATION - MAGISTRATES' COURT & PLEA IN COUNTY COURT..."
    ],
  },
  {
    label: "Indictable - Committal and or Plea in the Supreme Court",
    patterns: [
      "INDICTABLECOMMITTALANDORPLEAINTHESUPREMECOURT",
      "COMMITTALANDPLEASUPREMECOURT",
      "INDICTABLESUPREMEPLEA",
      // Application request scope heading for SC plea:
      "COMMITTALMENTIONGENERALPREPARATIONMAGISTRATESCOURTPLEAINSUPREMECOURT",
    ],
  },
  { label: "Indictable - Contested Committal", patterns: ["INDICTABLECONTESTEDCOMMITTAL", "CONTESTEDCOMMITTAL"] },
  { label: "Indictable - Ground Rules Hearing", patterns: ["INDICTABLEGROUNDRULESHEARING"] },
  { label: "Indictable - Bail (Magistrates Court)", patterns: ["INDICTABLEBAILMAGISTRATESCOURT"] },
  { label: "County Court Bail", patterns: ["COUNTYCOURTBAIL"] },
  { label: "Supreme Court Bail", patterns: ["SUPREMECOURTBAIL"] },
  { label: "Special Disbursement", patterns: ["SPECIALDISBURSEMENT", "SPECIALDISBURSMENT"] },
  { label: "Disbursement - Ballistic Report", patterns: ["BALLISTICREPORT"] },
  { label: "Disbursement - Car Collision Experts", patterns: ["CARCOLLISIONEXPERTS"] },
  { label: "Disbursement - Engineer Report", patterns: ["ENGINEERREPORT"] },
  { label: "Disbursement - Handwriting Expert", patterns: ["HANDWRITINGEXPERT"] },
  { label: "Disbursement - Medical Report - Doctor", patterns: ["MEDICALREPORTDOCTOR"] },
  { label: "Disbursement - Medical Report - Hospital", patterns: ["MEDICALREPORTHOSPITAL"] },
  { label: "Disbursement - Neuropsychological Report", patterns: ["NEUROPSYCHOLOGICALREPORT"] },
  { label: "Disbursement - Non-attendance Fee", patterns: ["NONATTENDANCEFEE"] },
  { label: "Disbursement - Other Disbursement", patterns: ["OTHERDISBURSEMENT"] },
  { label: "Disbursement - Pathologist Report", patterns: ["PATHOLOGISTREPORT"] },
  { label: "Disbursement - Psychiatric Attendance at City Court - Full Day", patterns: ["PSYCHIATRICATTENDANCEATCITYCOURTFULLDAY", "PSYCHIATRICATTENANDANCEATCITYCOURTFULLDAY"] },
  { label: "Disbursement - Psychiatric Attendance at City Court - Half Day", patterns: ["PSYCHIATRICATTENDANCEATCITYCOURTHALFDAY", "PSYCHIATRICATTENANDANCEATCITYCOURTHALFDAY"] },
  { label: "Disbursement - Psychiatric Attendance at Country Court - Full Day", patterns: ["PSYCHIATRICATTENDANCEATCOUNTRYCOURTFULLDAY", "PSYCHIATRICATTENANDANCEATCOUNTRYCOURTFULLDAY"] },
  { label: "Disbursement - Psychiatric Attendance at Country Court - Half Day", patterns: ["PSYCHIATRICATTENDANCEATCOUNTRYCOURTHALFDAY", "PSYCHIATRICATTENANDANCEATCOUNTRYCOURTHALFDAY"] },
  { label: "Disbursement - Psychiatric Doli Incapax", patterns: ["PSYCHIATRICDOLIINCAPAX", "PSYCHIATIRCDOLIINCAPAX"] },
  { label: "Disbursement - Psychiatric Evidentiary", patterns: ["PSYCHIATRICEVIDENTIARY", "PSYCHIATIRCEVIDENTIARY"] },
  { label: "Disbursement - Psychiatric New Assessment", patterns: ["PSYCHIATRICNEWASSESSMENT", "PSYCHIATIRCNEWASSESSMENT"] },
  { label: "Disbursement - Psychiatric New Assessment Including Jail", patterns: ["PSYCHIATRICNEWASSESSMENTINCLUDINGJAIL", "PSYCHIATIRCNEWASSESSMENTINCLUDINGJAIL"] },
  { label: "Disbursement - Psychiatric Supplementary", patterns: ["PSYCHIATRICSUPPLEMENTARY"] },
  { label: "Disbursement - Psychiatric Treating", patterns: ["PSYCHIATRICTREATING"] },
  { label: "Disbursement - Psychological Attendance at City Court - Full Day", patterns: ["PSYCHOLOGICALATTENDANCEATCITYCOURTFULLDAY", "PSYCHOLOGICALATTENANDANCEATCITYCOURTFULLDAY"] },
  { label: "Disbursement - Psychological Attendance at City Court - Half Day", patterns: ["PSYCHOLOGICALATTENDANCEATCITYCOURTHALFDAY", "PSYCHOLOGICALATTENANDANCEATCITYCOURTHALFDAY"] },
  { label: "Disbursement - Psychological Attendance at Country Court - Full Day", patterns: ["PSYCHOLOGICALATTENDANCEATCOUNTRYCOURTFULLDAY", "PSYCHOLOGICALATTENANDANCEATCOUNTRYCOURTFULLDAY"] },
  { label: "Disbursement - Psychological Attendance at Country Court - Half Day", patterns: ["PSYCHOLOGICALATTENDANCEATCOUNTRYCOURTHALFDAY", "PSYCHOLOGICALATTENANDANCEATCOUNTRYCOURTHALFDAY"] },
  { label: "Disbursement - Psychological Evidentiary", patterns: ["PSYCHOLOGICALEVIDENTIARY"] },
  { label: "Disbursement - Psychological New Assessment", patterns: ["PSYCHOLOGICALNEWASSESSMENT"] },
  { label: "Disbursement - Psychological New Assessment Including Jail", patterns: ["PSYCHOLOGICALNEWASSESSMENTINCLUDINGJAIL"] },
  { label: "Disbursement - Psychological Report with Risk Assessment", patterns: ["PSYCHOLOGICALREPORTWITHRISKASSESSMENT"] },
  { label: "Disbursement - Psychological Supplementary", patterns: ["PSYCHOLOGICALSUPPLEMENTARY"] },
  { label: "Disbursement - Psychological Treating", patterns: ["PSYCHOLOGICALTREATING"] },
  { label: "Disbursement - Psychological Doli Incapax", patterns: ["PSYCHOLOGICALDOLIINCAPAX", "PSYCHOLOGICALDOLIINCAPAX"] },
  { label: "County Court Breach", patterns: ["COUNTYCOURTBREACH", "BREACHPROCEEDINGSCOUNTYCOURT"] },
  { label: "Supreme Court Breach", patterns: ["SUPREMECOURTBREACH", "BREACHPROCEEDINGSSUPREMECOURT"] },
  { label: "County Court - Special Hearing", patterns: ["COUNTYCOURTSPECIALHEARING"] },
  { label: "County Court - Section 198A", patterns: ["COUNTYCOURTSECTION198A", "COUNTYCOURTS198A"] },
  { label: "County Court - Active Case Management (Pre Trial)", patterns: ["COUNTYCOURTACTIVECASEMANAGEMENTPRETRIAL", "COUNTYCOURTACTIVECASEMANAGMENTPRETRIAL"] },
  { label: "County Court - Trial", patterns: ["COUNTYCOURTTRIAL"] },
  { label: "Supreme Court - Special Hearing", patterns: ["SUPREMECOURTSPECIALHEARING"] },
  { label: "Supreme Court - s198B/Case Management", patterns: ["SUPREMECOURTS198BCASEMANAGEMENT", "SUPREMECOURTS198BCASEMANAGMENT"] },
  { label: "Supreme Court Trial", patterns: ["SUPREMECOURTTRIAL"] },
  { label: "Additional Prep", patterns: ["ADDITIONALPREP"] },
  { label: "County Court Appeal - Sentence", patterns: ["COUNTYCOURTAPPEALSENTENCE"] },
  { label: "County Court Appeal - Conviction or Conviction/Sentence", patterns: ["COUNTYCOURTAPPEALCONVICTIONORCONVICTIONSENTENCE"] },
  { label: "Court of Appeal - Leave Application", patterns: ["COURTOFAPPEALLEAVEAPPLICATION"] },
  { label: "Court of Appeal - Appeal against Sentence", patterns: ["COURTOFAPPEALAPPEALAGAINSTSENTENCE"] },
  { label: "Court of Appeal - Appeal against Conviction or Conviction and Sentence", patterns: ["COURTOFAPPEALAPPEALAGAINSTCONVICTIONORCONVICTIONANDSENTENCE"] },
  { label: "Court of Appeal - DPP Appeal", patterns: ["COURTOFAPPEALDPPAPPEAL"] },
  { label: "Interlocutory Appeal", patterns: ["INTERLOCUTORYAPPEAL", "INTERLOCUTORYAPPEAK"] },
  { label: "High Court Appeal", patterns: ["HIGHCOURTAPPEAL"] },
  { label: "Circuit Fees - Committals, bail, pleas and appeals in the Supreme and County Court", patterns: ["CIRCUITFEESCOMMITTALSBAILPLEASANDAPPEALSINTHESUPREMEANDCOUNTYCOURT"] },
  { label: "Circuit Fees - Trials in the Supreme and County Court", patterns: ["CIRCUITFEESTRIALSINTHESUPREMEANDCOUNTYCOURT"] },
  { label: "Crimes Mental Impairment - Post", patterns: ["CRIMESMENTALIMPAIRMENTPOST"] },
  { label: "Emergency detention orders", patterns: ["EMERGENCYDETENTIONORDERS"] },
  { label: "Detention orders / Commonwealth continuing detention orders", patterns: ["DETENTIONORDERSCOMMONWEALTHCONTINUINGDETENTIONORDERS"] },
  { label: "Supervision orders / Commonwealth extended supervision orders or control orders", patterns: ["SUPERVISIONORDERSCOMMONWEALTHEXTENDEDSUPERVISIONORDERSORCONTROLORDERS"] },
  { label: "Application for review brought by a person subject to an order", patterns: ["APPLICATIONFORREVIEWBROUGHTBYAPERSONSUBJECTTOANORDER"] },
  { label: "Crimes (Mental Impairment and Unfitness to be Tried) Act matters", patterns: ["CRIMESMENTALIMPAIRMENTANDUNFITNESSTOBETRIEDACTMATTERS"] },
  { label: "Children Court - Bail", patterns: ["CHILDRENCOURTBAIL", "CHILDRENSCOURTBAIL"] },
  { label: "Childrens Court - Single", patterns: ["CHILDRENSCOURTSINGLE", "CHILDRENCOURTSINGLE"] },
  { label: "Childrens Court - Consol", patterns: ["CHILDRENSCOURTCONSOL", "CHILDRENCOURTCONSOL"] },
  { label: "Childrens Court - Increase to consol", patterns: ["CHILDRENSCOURTINCREASETOCONSOL", "CHILDRENCOURTINCREASETOCONSOL"] },
  { label: "Childrens Court - Committal and Plea in the County Court", patterns: ["CHILDRENSCOURTCOMMITTALANDPLEAINTHECOUNTYCOURT", "CHILDRENCOURTCOMMITTALANDPLEAINTHECOUNTYCOURT"] },
  { label: "Childrens Court - Committal and Plea in the Supreme Court", patterns: ["CHILDRENSCOURTCOMMITTALANDPLEAINTHESUPREMECOURT", "CHILDRENCOURTCOMMITTALANDPLEAINTHESUPREMECOURT"] },
  { label: "Childrens Court - Serious Indictable Crime", patterns: ["CHILDRENSCOURTSERIOUSINDICTABLECRIME", "CHILDRENCOURTSERIOUSINDICTABLECRIME"] },
  { label: "Bail Application", patterns: ["MAGISTRATESCOURTBAILAPPLICATION", "BAILAPPLICATION"] },
  { label: "Disbursement - Psychological Assessment and Report (Not in custody)", patterns: ["PSYCHOLOGICALASSESSMENTANDREPORTNOTINCUSTODY"] },
  { label: "Disbursement - Psychological Assessment and Report (In custody)", patterns: ["PSYCHOLOGICALASSESSMENTANDREPORTINCUSTODY", "PSYCHOLOGICALASSESSMENTANDREPORTINCLUDINGJAILVISIT"] },
];

function inferAidTypes(text) {
  const compact = buildCompactUpper(text);
  const aidTypes = AID_TYPE_CATALOG.filter(({ patterns }) => patterns.some((pattern) => compact.includes(pattern))).map(({ label }) => label);

  if (aidTypes.some((label) => label.startsWith("Disbursement -")) && !aidTypes.includes("Special Disbursement")) {
    aidTypes.unshift("Special Disbursement");
  }

  return Array.from(new Set(aidTypes));
}

function extractApplicationRequestScopeOfWorkText(text = "") {
  // We locate the scope section by finding the exact phrase "Please check that you
  // have the correct grant of aid." which appears only in the Scope of Work section
  // of VLA application requests (not in the guidelines selection page).
  //
  // IMPORTANT: the old regex used [A-Z0-9\s\-\/\(\)']+? which broke on "&" (ampersand)
  // in headings like "MAGISTRATES' COURT & PLEA IN COUNTY COURT IF REQUIRED",
  // causing the regex to capture nothing or only a partial heading.
  // We now use string-index search + slice to avoid this entirely.

  const PLEASE_CHECK_PHRASE = "Please check that you have the correct grant of aid.";
  const pleaseIdx = text.indexOf(PLEASE_CHECK_PHRASE);
  if (pleaseIdx >= 0) {
    const afterPhrase = text.slice(pleaseIdx + PLEASE_CHECK_PHRASE.length);

    // Try to find a clean terminator marking the end of the scope section
    const TERMINATORS = [
      "Waiver of Proof of Means",
      "MTC3",
      "Practitioners are required to certify",
      "PLEASE NOTE:",
      "PROFESSIONAL COSTS REQUESTED",
      "COURT HEARINGS",
      "\nSignature",
    ];
    let endIdx = -1;
    for (const term of TERMINATORS) {
      const idx = afterPhrase.indexOf(term);
      if (idx >= 0 && (endIdx < 0 || idx < endIdx)) {
        endIdx = idx;
      }
    }

    // Return up to the terminator; if no terminator found, take a 6000-char slice
    // (enough to cover the full scope section of a multi-grant application)
    return endIdx >= 0 ? afterPhrase.slice(0, endIdx) : afterPhrase.slice(0, 6000);
  }

  // Secondary fallback: find "SCOPE OF WORK" and take a generous slice from there
  const scopeIdx = text.search(/SCOPE OF WORK/i);
  if (scopeIdx >= 0) {
    return text.slice(scopeIdx, scopeIdx + 6000);
  }

  // Last resort: return the full text
  return text;
}

function extractApplicationRequestScopeOfWorkLines(text = "") {
  return extractApplicationRequestScopeOfWorkText(text)
    .split("\n")
    .map(cleanLine)
    .filter(Boolean);
}

/**
 * Extracts the bold document-level heading from page 1 of an application request,
 * e.g. "ADULT - INDICTABLE (COMMITTAL/PLEA/BAIL)" or "ADULT - SUMMARY".
 * This is used as the human-readable aidType label for the planner.
 */
function extractApplicationRequestDocumentHeading(lines = []) {
  for (let i = 0; i < Math.min(lines.length, 40); i++) {
    const line = cleanLine(lines[i]);
    const upper = line.toUpperCase();
    if (
      /ADULT\s*[-\u2013]\s*INDICTABLE/.test(upper) ||
      /CHILDREN\s*[-\u2013]\s*INDICTABLE/.test(upper) ||
      /ADULT\s*[-\u2013]\s*SUMMARY/.test(upper) ||
      /CHILDREN[S']?\s*COURT\s*[-\u2013]/.test(upper) ||
      /COURT\s*OF\s*APPEAL/.test(upper) ||
      /HIGH\s*COURT\s*(CRIMINAL|APPEAL)/i.test(upper) ||
      /FAMILY\s*VIOLENCE\s*[-\u2013]/.test(upper) ||
      /PERSONAL\s*SAFETY\s*INTERVENTION/.test(upper) ||
      /SERIOUS\s*OFFENDER/.test(upper) ||
      /MENTAL\s*IMPAIRMENT/.test(upper)
    ) {
      // Exclude lines that are just navigation/metadata
      if (line.length > 10 && !/application source|submitted by|private practitioner/i.test(line)) {
        return line;
      }
    }
  }
  return "";
}

function inferAidTypesFromLines(lines, text, documentType = "") {
  let aidTypes = [];

  if (documentType === "application_request") {
    const scopeLines = extractApplicationRequestScopeOfWorkLines(text);

    // Collect ALL matching aid types from ALL scope of work lines.
    // (Previously used .find() which stopped at the first match, missing the other grant sections.)
    for (const line of scopeLines) {
      const types = inferAidTypes(line);
      for (const t of types) {
        if (!aidTypes.includes(t)) aidTypes.push(t);
      }
    }

    // Check for Summary Single heading ONLY within the scope of work lines — not the full document.
    // (The full 11-page application request includes a guidelines list that contains
    //  "SUMMARY ... FIVE DEFENDED DAYS ... MAGISTRATES" as an unselected option,
    //  which would otherwise be a false positive.)
    const summaryHeadingInScope = scopeLines.findIndex(
      (line) =>
        /SUMMARY/.test(line) && /MAGISTRATES/.test(line) && /FIVE DEFENDED DAYS/.test(line)
    );
    if (summaryHeadingInScope >= 0 && !aidTypes.includes("Summary Single")) {
      aidTypes.unshift("Summary Single");
    }
  } else {
    aidTypes = inferAidTypes(text);

    // For non-application documents (grant approval letters etc.) scan all lines.
    const summaryHeadingIndex = findLineIndex(
      lines,
      (line) =>
        /SUMMARY/.test(line) &&
        /MAGISTRATES/.test(line) &&
        /FIVE DEFENDED DAYS/.test(line)
    );
    if (summaryHeadingIndex >= 0 && !aidTypes.includes("Summary Single")) {
      aidTypes.unshift("Summary Single");
    }
  }

  return Array.from(new Set(aidTypes));
}

function buildConflictFlags({ inCustody, aidTypes }) {
  const flags = [];
  if (
    inCustody === "YES" &&
    aidTypes.some((item) => /psychological assessment and report \(not in custody\)/i.test(item))
  ) {
    flags.push({
      id: "custody-psych-report-mismatch",
      level: "error",
      message:
        "The application records the client as in custody, but the scope of work includes Psychological Assessment and Report (Not in custody).",
      actionLabel: "Set action to manual review",
      recommendedStatus: "Needs manual review",
    });
  }
  return flags;
}

function detectDocumentType(text = "", compact = "") {
  if (compact.includes("APPLICATIONREQUEST")) return "application_request";

  // ATLAS claim form detection MUST run before grant approval check —
  // ATLAS claim forms include the VLA grant number as a reference field,
  // so "VLAGRANTNUMBER" alone cannot distinguish them from approval letters.
  // An ATLAS claim confirmation ("Recipient created tax invoice") always has these headings;
  // its other wording varies, so this does not rely on it.
  if (compact.includes("RECIPIENTCREATEDTAXINVOICE") && compact.includes("CLAIMDETAILS")) {
    return "claim_letter";
  }

  if (
    compact.includes("WORKITEMS") ||
    compact.includes("THISCLAIM") ||
    compact.includes("CLAIMSUMMARY") ||
    compact.includes("EFFECTIVEDATERANGE") ||
    compact.includes("UNITSTOBECLAIMED") ||
    compact.includes("CLAIMFORLEGALASSISTANCE") ||
    compact.includes("CLAIMFORPAYMENT") ||
    compact.includes("ATLASCLAIM") ||
    compact.includes("CLAIMNUMBER") ||
    compact.includes("BILLINGSTATEMENT") ||
    compact.includes("FEECLAIM") ||
    compact.includes("STATEMENTOFACCOUNT") ||
    compact.includes("INVOICENUMBER") ||
    (compact.includes("CLAIM") && compact.includes("TOTALPAYABLE")) ||
    (compact.includes("CLAIM") && compact.includes("TOTALFEES"))
  ) {
    return "claim_letter";
  }

  if (
    compact.includes("LEGALASSISTANCEISAPPROVED") ||
    compact.includes("APPROVEDLEGALASSISTANCE") ||
    compact.includes("VLAGRANTNUMBER")
  ) {
    return "grant_approval_letter";
  }

  return "unknown";
}

function extractApprovalLetterAidNumber(compact = "") {
  const match = compact.match(/VLAGRANTNUMBER(\d{2}A\d{6})/);
  return match?.[1] || "";
}

function extractApprovalLetterCourt(compact = "") {
  if (compact.includes("MAGISTRATESCOURT")) return "Magistrates' Court";
  if (compact.includes("COUNTYCOURT")) return "County Court";
  if (compact.includes("SUPREMECOURT")) return "Supreme Court";
  if (compact.includes("COURTOFAPPEAL")) return "Court of Appeal";
  return "";
}

function extractApprovalLetterLocation(text = "", compact = "") {
  const match = compact.match(/CRIMINALLAWMATTERINTHE(?:MAGISTRATESCOURT|COUNTYCOURT|SUPREMECOURT|COURTOFAPPEAL)IN([A-Z]+?)ASSISTANCEISEFFECTIVEFROM/);
  if (match?.[1]) {
    const raw = match[1];
    return raw.charAt(0) + raw.slice(1).toLowerCase();
  }
  if (/HEIDELBERG/i.test(text)) return "Heidelberg";
  return "";
}

function buildParsedAlerts({ documentType, compactText, listingType }) {
  const alerts = [];
  if (documentType !== "grant_approval_letter") return alerts;

  if (compactText.includes("DOESNOTINCLUDEASSISTANCEFORACONTESTEDHEARING")) {
    alerts.push({
      level: listingType === "Trial" ? "warning" : "info",
      title: "Contested hearing needs separate grant",
      message:
        "This grant does not include assistance for a contested hearing. A separate grant is required before the contested hearing proceeds.",
      nextAction: "Apply for separate contested hearing grant before hearing",
      recommendedStatus: "extension_review",
    });
  }

  if (compactText.includes("WILLNOTCOVER") && compactText.includes("APPEARANCEATAMENTIONHEARING")) {
    alerts.push({
      level: listingType === "Mention" ? "warning" : "info",
      title: "Mention appearances may not be covered",
      message:
        "The approval letter says this grant will generally not cover an appearance at a mention hearing or a client-requested adjournment, except in limited circumstances.",
      nextAction: "Check whether a special mention fee or different grant action is needed",
      recommendedStatus: "manual_review",
    });
  }

  if (compactText.includes("SPECIALMENTIONSCANONLYBECLAIMEDINVERYLIMITEDCIRCUMSTANCES")) {
    alerts.push({
      level: "info",
      title: "Special mention fees are limited",
      message:
        "The approval letter says special mentions can only be claimed in very limited circumstances under the VLA notes on the guidelines.",
      nextAction: "Confirm the listing qualifies before relying on a special mention fee",
      recommendedStatus: "manual_review",
    });
  }

  return alerts;
}

export async function parseAidLetterFile(file) {
  const text = await extractTextFromPdfFile(file);
  const annotatedOverrides = await extractAnnotatedFieldOverrides(file).catch(() => ({}));
  const normalizedText = text.replace(/\u0000/g, " ").replace(/\s+\n/g, "\n");
  const compactText = buildCompactUpper(normalizedText);
  const lines = getLines(normalizedText);
  const documentType = detectDocumentType(normalizedText, compactText);
  // DIAGNOSTIC — remove after debugging
  console.log("[EMMA-PARSER v2] documentType:", documentType, "| file:", file.name, "| first 5 lines:", lines.slice(0, 5));

  const statusIndex = findLineIndex(lines, (line) => line.toLowerCase() === "status");
  const fileIndex = findLineIndex(lines, (line) => line.toLowerCase() === "file");
  const idIndex = findLineIndex(lines, (line, index) => line.toLowerCase() === "id" && index === fileIndex + 1);
  const matterTypeIndex = findLineIndex(lines, (line) => line.toLowerCase() === "matter type");
  const courtIndex = findLineIndex(lines, (line) => line.toLowerCase().includes("which court/tribunal do you have to go"));
  const locationIndex = findLineIndex(lines, (line) => line.toLowerCase().includes("where is the court/tribunal"));
  const hearingTypeIndex = findLineIndex(lines, (line) => line.toLowerCase().includes("what type of hearing is it"));
  const hearingDateIndex = findLineIndex(lines, (line) => line.toLowerCase().includes("when is the next hearing date"));
  const custodyIndex = findLineIndex(lines, (line) => line.toLowerCase().includes("are you in custody or detention"));

  const statusRaw =
    getNextMeaningfulLine(lines, statusIndex, { skip: ["Submitted", "by", "Submitted by"] }) ||
    matchAfterLabel(normalizedText, "Status", ["Submitted by", "Application Source"]);
  const applicationRequestStatus = extractApplicationRequestStatus(statusRaw, compactText);
  const fileId =
    extractAidNumber(normalizedText) ||
    getNextMeaningfulLine(lines, idIndex, { skip: ["ASHLEY WELSH", "Extension", "0", "Status"] }) ||
    matchAfterLabel(normalizedText, "File ID", ["Extension", "Status"]);
  let extensionNumber = extractExtensionNumber(lines, normalizedText, compactText, documentType);

  // Fallback: extract extension number from VLA filename pattern
  // e.g. "E_25A834943_1_2317962_Application_Request.pdf" → extension "1"
  if (!extensionNumber || extensionNumber === "0") {
    const fnMatch = file.name.match(/^[A-Z]_[0-9A-Z]+_(\d+)_\d+_/i);
    if (fnMatch && Number(fnMatch[1]) > 0) {
      extensionNumber = fnMatch[1];
    }
  }

  const matterTypeRaw =
    getNextMeaningfulLine(lines, matterTypeIndex, { skip: ["Plea", "CJEP / CAN"] }) ||
    matchAfterLabel(normalizedText, "Matter Type", ["Plea", "CJEP", "CAN"]);
  const courtRaw =
    getNextMeaningfulLine(lines, courtIndex, { skip: ["to?", "Where is the court/tribunal?"] }) ||
    matchAfterLabel(normalizedText, "Which court/tribunal do you have to go to?", ["Where is the court/tribunal?"]);
  const location =
    getNextMeaningfulLine(lines, locationIndex, { skip: ["What is your role in these proceedings?"] }) ||
    matchAfterLabel(normalizedText, "Where is the court/tribunal?", ["What is your role in these proceedings?"]);
  const hearingTypeRaw =
    getNextMeaningfulLine(lines, hearingTypeIndex, { skip: ["Court Proceedings Number"] }) ||
    matchAfterLabel(normalizedText, "What type of hearing is it?", ["Court Proceedings Number", "Is this the main hearing"]);
  const hearingDateRaw =
    getNextMeaningfulLine(lines, hearingDateIndex, { skip: ["Which court/tribunal do you have to go", "to?"] }) ||
    matchAfterLabel(normalizedText, "When is the next hearing date?", ["Which court/tribunal do you have to go to?"]);
  const custodyRaw =
    getNextMeaningfulLine(lines, custodyIndex, { skip: ["Partner Details", "Custody Details"] }) ||
    matchAfterLabel(normalizedText, "Are you in custody or detention?", ["Partner Details", "Partner / Dependants"]);

  // Approval letters (initial and extension) name the handbook fee table for each approved
  // item; that decides the grant type, not the wording.
  const approval = documentType === "grant_approval_letter"
    ? readApprovalLetter(await extractPositionalLines(file).catch(() => []))
    : null;
  const approvedAidTypes = [...new Set((approval?.approvedGrants || []).map((grant) => grant.aidType).filter(Boolean))];
  // ATLAS claims name the grant and list each fee claimed in VLA's own fee names.
  const claimLines = documentType === "claim_letter" ? await extractPositionalLines(file).catch(() => []) : [];
  const claim = isAtlasClaim(claimLines) ? readAtlasClaim(claimLines) : null;
  const claimName = claim?.clientName
    ? claim.clientName.toLowerCase().replace(/\b[a-z]/g, (letter) => letter.toUpperCase()).split(" ")
    : [];

  const parsedCosts = extractAllProfessionalCosts(lines, normalizedText);
  const aidTypes =
    Array.isArray(annotatedOverrides.aidTypeList) && annotatedOverrides.aidTypeList.length > 0
      ? annotatedOverrides.aidTypeList
      : (() => {
          if (documentType === "application_request" && parsedCosts.length > 0) {
            return parsedCosts;
          }
          if (approvedAidTypes.length > 0) return approvedAidTypes;
          const templateAidTypes = documentType === "application_request" ? extractTemplateAidType(lines) : [];
          return templateAidTypes.length > 0
            ? templateAidTypes
            : inferAidTypesFromLines(lines, normalizedText, documentType);
        })();
  const templateGuideline = documentType === "application_request" ? extractTemplateGuideline(lines) : "";
  const approvalAidNumber = extractApprovalLetterAidNumber(compactText);
  const approvalCourt = extractApprovalLetterCourt(compactText);
  const approvalLocation = extractApprovalLetterLocation(normalizedText, compactText);
  const normalizedCourt = annotatedOverrides.court || approvalCourt || normalizeCourt(courtRaw);
  const inferredListingType =
    documentType === "application_request"
      ? extractTemplateListingType(annotatedOverrides.listingTypeRaw || hearingTypeRaw, normalizedCourt)
      : annotatedOverrides.listingType || normalizeListingType(annotatedOverrides.listingTypeRaw || hearingTypeRaw, normalizedCourt);
  const approvalNameParts = approval?.clientFullName ? approval.clientFullName.split(" ").filter(Boolean) : claimName;
  const clientFirstName = extractClientFirstName(lines, normalizedText) || (approvalNameParts.length > 1 ? approvalNameParts.slice(0, -1).join(" ") : approvalNameParts[0] || "");
  const clientLastName = extractClientLastName(lines, normalizedText) || (approvalNameParts.length > 1 ? approvalNameParts.at(-1) : "");
  const clientDateOfBirth = extractClientDateOfBirth(lines, normalizedText);
  let dobString = "";
  if (clientDateOfBirth) {
    const parts = clientDateOfBirth.split("-");
    if (parts.length === 3) {
      dobString = `${parts[2]}/${parts[1]}/${parts[0]}`;
    }
  }
  const matterGroup = extractMatterGroup(lines, normalizedText);
  const practitionerReference = extractPractitionerReference(lines, normalizedText) || approval?.lawyerReference || "";

  const parsedAlerts = buildParsedAlerts({
    documentType,
    compactText,
    listingType: inferredListingType,
  });
  const conflictFlags = buildConflictFlags({
    inCustody: cleanLine(custodyRaw).toUpperCase(),
    aidTypes,
  });

  return {
    rawText: normalizedText,
    fields: {
      clientFirstName,
      clientLastName,
      clientDateOfBirth,
      clientFullName: clientFirstName && clientLastName ? `${clientFirstName} ${clientLastName}` : clientFirstName || clientLastName,
      headCharge: matterGroup,
      practitionerReference,
      matterType:
        documentType === "grant_approval_letter"
          ? "Criminal"
          : normalizeMatterType(normalizedText, matterTypeRaw),
      // For application requests, prefer the bold document heading from page 1
      // (e.g. "ADULT - INDICTABLE (COMMITTAL/PLEA/BAIL)") as the human-readable label.
      // Fall back to parsedCosts / aidTypes if no heading is detected.
      aidType: annotatedOverrides.aidType ||
        (documentType === "application_request"
          ? (extractApplicationRequestDocumentHeading(lines) ||
              (parsedCosts.length > 0 ? formatProfessionalCostsList(parsedCosts) : formatProfessionalCostsList(aidTypes)))
          : formatProfessionalCostsList(aidTypes)),
      aidTypeList: aidTypes,
      guideline: annotatedOverrides.guideline || templateGuideline || "",
      aidNumber: cleanLine(annotatedOverrides.aidNumber || approval?.aidNumber || claim?.aidNumber || approvalAidNumber || fileId),
      extensionNumber: annotatedOverrides.extensionNumber ?? (approval?.extensionNumber || claim?.extensionNumber || extensionNumber),
      court: normalizedCourt,
      location: cleanLine(annotatedOverrides.location || approvalLocation || location),
      listingType: inferredListingType,
      listingTypeRaw: cleanLine(annotatedOverrides.listingTypeRaw || hearingTypeRaw),
      listingDate:
        documentType === "grant_approval_letter"
          ? ""
          : annotatedOverrides.listingDate || parseDate(hearingDateRaw),
      applicationStatus:
        documentType === "grant_approval_letter"
          ? "Aid granted"
          : annotatedOverrides.applicationStatus || inferApplicationStatus(applicationRequestStatus),
      documentType,
      statusRaw:
        documentType === "grant_approval_letter"
          ? "APPROVED"
          : applicationRequestStatus,
      custodyStatus:
        documentType === "grant_approval_letter"
          ? ""
          : cleanLine(annotatedOverrides.custodyStatus || cleanLine(custodyRaw).split(" ")[0]),
      dateApplied:
        annotatedOverrides.dateApplied || parseApplicationEffectiveDate(normalizedText, dobString),
      effectiveDate:
        documentType === "grant_approval_letter"
          ? approval?.effectiveDate || parseApprovalEffectiveDate(normalizedText)
          : "",
      approvedGrants: approval?.approvedGrants || [],
      grantExclusions: approval?.exclusions || [],
      grantConditions: approval?.conditions || [],
      reconsiderationBy: approval?.reconsiderationBy || "",
      contribution: approval?.contribution || "",
      claim: claim ? { ...claim, items: undefined } : null,
      claimItems: claim?.items || [],
    },
    parsedAlerts,
    conflictFlags,
  };
}

function fileToBase64(file) {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => {
      const result = `${reader.result || ""}`;
      const base64 = result.includes(",") ? result.split(",")[1] : "";
      if (!base64) {
        reject(new Error("Could not encode the uploaded aid letter."));
        return;
      }
      resolve(base64);
    };
    reader.onerror = () => reject(new Error("Could not read the uploaded aid letter."));
    reader.readAsDataURL(file);
  });
}

// Letters are read here, in the browser, first. This reader knows the approval letter's fee
// tables, exclusions and conditions, the extension number and ATLAS claim rows; the server's
// older reader (/api/parse-aid-letter) returns none of them, and read first it hid them from
// the reminders. The server is asked only when no text can be read here.
export async function parseAidLetterUpload(file) {
  try {
    return await parseAidLetterFile(file);
  } catch (clientError) {
    try {
      const base64 = await fileToBase64(file);
      const response = await fetch("/api/parse-aid-letter", {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify({ base64 }),
      });
      const payload = await response.json().catch(() => ({}));
      if (!response.ok || !payload?.fields) throw new Error(payload?.error || "");
      return payload;
    } catch {
      throw clientError;
    }
  }
}
